/**
 * DisputedCard — single disputed-product card (§11.2 / DM-22).
 *
 * Block M1a-FE (TZ 2026-07-29). Card content:
 *   - product name (из raw_data первой key-column)
 *   - eye icon → DisputedEyeModal
 *   - top-2 path chips
 *   - expand toggle (reveal all candidates)
 *   - Confirm button → useResolveRow
 *
 * NO price / brand required (§11.6).
 */
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ChevronDown, ChevronUp, Eye, Sparkles } from 'lucide-react';

import {
  useResolveRow,
  type MatchingCategoryCandidate,
} from '@/hooks/useMatching';
import { cn } from '@/lib/utils';

export interface DisputedCardData {
  row_id: string;
  /** First non-internal raw value (для product name, §11.2). */
  displayName: string;
  /** Candidates ordered by score desc (берём из BE payload). */
  candidates: MatchingCategoryCandidate[];
  /** row_index если есть (для identity display). */
  rowIndex?: number | null;
}

export interface DisputedCardProps {
  data: DisputedCardData;
  datasetId: string | null;
  onOpenEye: (data: DisputedCardData) => void;
}

export function DisputedCard({
  data,
  datasetId,
  onOpenEye,
}: DisputedCardProps) {
  const { t } = useTranslation();
  const [expanded, setExpanded] = useState(false);
  const [chosenIdx, setChosenIdx] = useState<number | null>(null);
  const resolveRow = useResolveRow();

  const { row_id, candidates, displayName, rowIndex } = data;
  const top2 = candidates.slice(0, 2);
  const rest = candidates.slice(2);

  function handleConfirm() {
    if (chosenIdx === null || !datasetId) return;
    const chosen = candidates[chosenIdx];
    if (!chosen) return;
    resolveRow.mutate({
      dataset_id: datasetId,
      row_id,
      category: { ...chosen, status: 'confirmed' },
    });
    setChosenIdx(null);
  }

  return (
    <article
      className={cn(
        'card shrink-0 w-[18rem] sm:w-[20rem] flex flex-col gap-2 p-3 snap-start',
        'border border-surface-700 hover:border-surface-500 transition-colors',
      )}
      data-testid="disputed-card"
      data-row-id={row_id}
    >
      <header className="flex items-start justify-between gap-2">
        <div className="min-w-0 flex-1">
          <div className="text-[10px] uppercase tracking-wider text-surface-500 font-mono">
            {t('process.matching.results.disputed.card.identity', {
              defaultValue: 'Row {index}',
              index: rowIndex ?? row_id.slice(0, 8),
            })}
          </div>
          <h3 className="text-sm font-semibold text-surface-100 truncate" title={displayName}>
            {displayName || '—'}
          </h3>
        </div>
        <button
          type="button"
          onClick={() => onOpenEye(data)}
          className="p-1 rounded-md text-surface-400 hover:text-primary-300 hover:bg-surface-800 transition-colors"
          title="Open details"
          aria-label={t('process.matching.results.disputed.eye.title', {
            defaultValue: 'Row details',
          })}
        >
          <Eye className="w-4 h-4" />
        </button>
      </header>

      <div className="space-y-1 flex-1">
        {candidates.length === 0 ? (
          <p className="text-xs text-surface-500 italic">
            {t('process.matching.results.disputed.empty', {
              defaultValue: 'No proposed categories.',
            })}
          </p>
        ) : (
          <>
            <ul className="space-y-1">
              {(expanded ? candidates : top2).map((c, i) => {
                const chosen = chosenIdx === i;
                return (
                  <li key={`${c.category_id}-${i}`}>
                    <button
                      type="button"
                      onClick={() => setChosenIdx(chosen ? null : i)}
                      className={cn(
                        'w-full text-left px-2 py-1.5 rounded-md border text-[11px] leading-snug transition-colors',
                        chosen
                          ? 'border-primary-500 bg-primary-500/10 text-primary-200'
                          : 'border-surface-700 bg-surface-900 text-surface-300 hover:border-surface-500',
                      )}
                      title={
                        chosen
                          ? t('process.matching.results.disputed.card.confirmTitle', {
                              defaultValue: 'Save this category for the row',
                            })
                          : ''
                      }
                    >
                      <div className="flex items-center justify-between gap-1">
                        <span className="truncate">
                          {(c.path_ua?.length ? c.path_ua : c.path_ru ?? []).join(' › ') ||
                            c.category_id}
                        </span>
                        <span className="font-mono text-[10px] text-surface-500">
                          {Math.round(c.score * 100)}%
                        </span>
                      </div>
                    </button>
                  </li>
                );
              })}
            </ul>
            {rest.length > 0 && (
              <button
                type="button"
                onClick={() => setExpanded((v) => !v)}
                className="flex items-center gap-1 text-[11px] text-primary-300 hover:text-primary-200 transition-colors"
              >
                {expanded ? (
                  <>
                    <ChevronUp className="w-3 h-3" />
                    {t('process.matching.results.disputed.card.collapse', {
                      defaultValue: 'Hide extra candidates',
                    })}
                  </>
                ) : (
                  <>
                    <ChevronDown className="w-3 h-3" />
                    {t('process.matching.results.disputed.card.expand', {
                      defaultValue: 'Show {n} more candidates',
                      n: rest.length,
                    })}
                  </>
                )}
              </button>
            )}
          </>
        )}
      </div>

      <button
        type="button"
        onClick={handleConfirm}
        disabled={chosenIdx === null || resolveRow.isPending || !datasetId}
        className={cn(
          'mt-1 flex items-center justify-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-colors',
          chosenIdx === null || !datasetId
            ? 'bg-surface-800 text-surface-500 cursor-not-allowed'
            : 'bg-success-strong hover:bg-success-hover text-white',
        )}
        title={t('process.matching.results.disputed.card.confirmTitle', {
          defaultValue: 'Save this category for the row',
        })}
      >
        <Sparkles className="w-3.5 h-3.5" />
        {t('process.matching.results.disputed.card.confirm', {
          defaultValue: 'Confirm',
        })}
      </button>
    </article>
  );
}
